import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import type { ClassSeries } from '../domain/types';
import { formatDuration, formatSeriesSummary } from '../domain/format';
import { useAsync, useRepository } from './repositoryContext';
import { EmptyState, ErrorBanner } from './components';
import { ClassForm } from './ClassForm';
import { ChevronRight } from './icons';

export function ClassesView(props: {
  /** Set when arriving from "Edit the whole series"; cleared once handled. */
  editSeriesId?: string | null;
  /** Set when arriving from the schedule's empty state. */
  startNew?: boolean;
  onFormClosed?: () => void;
}): JSX.Element {
  const repo = useRepository();
  const locations = useAsync(() => repo.listLocations(), []);
  const series = useAsync(() => repo.listSeries(), []);

  const [creating, setCreating] = useState(props.startNew === true);
  const [editingId, setEditingId] = useState<string | null>(props.editSeriesId ?? null);
  const [error, setError] = useState<Error | undefined>(undefined);

  const all = series.data ?? [];
  const editing = all.find((item) => item.id === editingId);
  const byId = new Map((locations.data ?? []).map((location) => [location.id, location]));

  function closeForm(): void {
    setCreating(false);
    setEditingId(null);
    props.onFormClosed?.();
  }

  async function setActive(item: ClassSeries, active: boolean): Promise<void> {
    try {
      await repo.updateSeries(item.id, { active });
      series.reload();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  async function remove(item: ClassSeries): Promise<boolean> {
    if (!confirm(`Delete "${item.title}"?`)) {
      return false;
    }
    try {
      await repo.deleteSeries(item.id);
      series.reload();
      return true;
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
      return false;
    }
  }

  // The form is a screen of its own, so it replaces the list rather than sitting
  // inside it — the mobile convention, and it keeps focus on one task.
  if (creating || editingId !== null) {
    // Mounting before the places have loaded would default the picker to
    // "somewhere new" even when the instructor already has places, and would
    // leave the summary without a place name.
    if (locations.data === undefined || series.data === undefined) {
      return <p class="muted">Loading…</p>;
    }
    return (
      <ClassForm
        locations={locations.data}
        series={editing}
        seriesCount={series.data.length}
        onSaved={() => {
          closeForm();
          series.reload();
          locations.reload();
        }}
        onCancel={closeForm}
        onArchiveToggle={
          editing === undefined
            ? undefined
            : async () => {
                await setActive(editing, !editing.active);
                closeForm();
              }
        }
        onDelete={
          editing === undefined
            ? undefined
            : async () => {
                const deleted = await remove(editing);
                if (deleted) {
                  closeForm();
                }
              }
        }
      />
    );
  }

  return (
    <section>
      <header class="screen-head">
        <h2>Classes</h2>
        <button
          type="button"
          class="primary"
          aria-label="Add a class"
          onClick={() => setCreating(true)}
        >
          + Add
        </button>
      </header>

      <ErrorBanner error={series.error ?? locations.error ?? error} />

      {!series.loading && all.length === 0 ? (
        <EmptyState title="No classes yet.">
          <p>Add the classes you teach and they'll show up on your schedule.</p>
          <button type="button" class="primary" onClick={() => setCreating(true)}>
            Add a class
          </button>
        </EmptyState>
      ) : null}

      <ul class="rows">
        {all.map((item) => {
          const place = byId.get(item.locationId);
          return (
            <li key={item.id}>
              <button type="button" class="row" onClick={() => setEditingId(item.id)}>
                <span
                  class="row-bar"
                  style={{ background: item.color ?? 'var(--muted)' }}
                  aria-hidden="true"
                />
                <span class="row-what">
                  <span class="row-title">
                    {item.title}
                    {item.active ? null : <span class="tag">Archived</span>}
                  </span>
                  <span class="row-place">
                    {formatSeriesSummary({
                      recurrence: item.recurrence,
                      startTime: item.startTime,
                      locationName: place?.name ?? 'No place',
                    })}
                  </span>
                  <span class="row-place">{formatDuration(item.durationMinutes)}</span>
                </span>
                <ChevronRight />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
